/**
 * Admin taxonomy sections — `PartnerTiersSection` + `CustomerPlansSection`.
 *
 * Phase 3 consolidated batch A–E features built into this file:
 *   - Section A  : per-card edit mode (default read-only summary; Edit toggles
 *                  the inline editor; Save/Cancel; Esc cancels). Applied to
 *                  BOTH tiers and plans.
 *   - Section B  : `search_placement_weight` dropped (server + UI); "Name" UI
 *                  label renamed to "Tier name" for tiers; numeric `sort_order`
 *                  removed from the tier form — order is set via drag-and-drop.
 *   - Section C  : badge_label → `badge_id` enum dropdown rendered as colored
 *                  pill + lucide icon, sourced from `/lib/badge_presets.js`.
 *   - Section E  : Example Payout panel on every tier card (live preview off
 *                  the draft fee_value while editing). Reads enabled platform
 *                  fees fetched once at the section level.
 *
 * Section D's "Fees" tab lives in `PlatformFeesSection.jsx`.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  DragDropContext,
  Droppable,
  Draggable,
} from "@hello-pangea/dnd";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  Loader2,
  Award,
  Users,
  Plus,
  Trash2,
  GripVertical,
  Star,
} from "lucide-react";
import {
  BADGE_OPTIONS,
  badgeMeta,
  badgeColorClasses,
  badgeIconComponent,
} from "@/lib/badge_presets";
import BadgeChip from "@/components/BadgeChip";

// ---------------------------------------------------------------------------
// Validation helpers — mirror `admin_taxonomy.py` constraints
// ---------------------------------------------------------------------------
const REQUIRED_NAME = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return "Required.";
  if (s.length > 30) return "30 characters max.";
  return null;
};
const INT_GTE_0 = (v) => {
  if (v === "" || v === null || v === undefined) return "Required.";
  const n = Number(v);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return "Whole number required.";
  if (n < 0) return "Must be 0 or more.";
  return null;
};
const FLOAT_GTE_0 = (v) => {
  if (v === "" || v === null || v === undefined) return "Required.";
  const n = Number(v);
  if (!Number.isFinite(n)) return "Number required.";
  if (n < 0) return "Must be 0 or more.";
  return null;
};
const PERCENT_0_100 = (v) => {
  const base = FLOAT_GTE_0(v);
  if (base) return base;
  if (Number(v) > 100) return "Must be 0–100.";
  return null;
};

// Tier fields shown in the editable grid. Missed-Items Combined Batch
// (Item 3) — Phase F Stage 2 removal: `monthly_price`, `fee_type`, and
// `fee_value` are dropped from the UI. The backend response schema is
// UNCHANGED (per Stage 2 dispatch contract) — bookings.py still reads
// `commission_pct` for the canonical commission rate and (once wired)
// `subscription_price_cents` for the tier subscription. Admin can no
// longer edit the deprecated fields; that's the point of Stage 2.
// `badge_id` renders via BadgeSelect. `sort_order` is hidden — set via
// drag-and-drop.
const TIER_FIELDS = [
  { key: "name", label: "Tier name", type: "text", validate: REQUIRED_NAME },
  // Admin Portal Consolidation Step 1 — per-tier Stripe Connect commission %
  // is the canonical rate read by `bookings.py::_resolve_tier_commission_pct`.
  // Surfacing it here lets admins tune it without dropping into Mongo.
  { key: "commission_pct", label: "Commission %", type: "percent", validate: PERCENT_0_100 },
];
const TIER_EXTRA_KEYS = ["badge_id", "perks"];

function validateBadgeId(v) {
  if (v == null || v === "") return null;
  if (!BADGE_OPTIONS.includes(v)) return "Pick a preset.";
  return null;
}

const PLAN_FIELDS = [
  { key: "name", label: "Name", type: "text", validate: REQUIRED_NAME },
  { key: "monthly_price", label: "Monthly price", type: "currency", validate: FLOAT_GTE_0 },
  { key: "member_discount_pct", label: "Member discount %", type: "percent", validate: PERCENT_0_100 },
  { key: "sort_order", label: "Sort order", type: "integer", validate: INT_GTE_0 },
];
const PLAN_BOOLEAN_FIELDS = [
  { key: "waive_securement_fee", label: "Waives securement fee" },
  { key: "priority_scheduling", label: "Priority scheduling" },
];

// ---------------------------------------------------------------------------
// Serialization helpers
// ---------------------------------------------------------------------------
// Admin Portal Consolidation Step 1 — `perks` is a List[str] in storage but
// rendered as a "one per line" textarea in the UI. These two helpers are
// the single conversion gate: array → "perk1\nperk2" for the input value,
// and the textarea string → cleaned string[] for the save payload.
function perksArrayToTextarea(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return "";
  return arr.map((s) => String(s ?? "").trim()).filter(Boolean).join("\n");
}
function perksTextareaToArray(s) {
  if (!s || typeof s !== "string") return [];
  return s
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}
function perksArraysEqual(a, b) {
  const aa = Array.isArray(a) ? a : [];
  const bb = Array.isArray(b) ? b : [];
  if (aa.length !== bb.length) return false;
  for (let i = 0; i < aa.length; i += 1) {
    if (String(aa[i] ?? "") !== String(bb[i] ?? "")) return false;
  }
  return true;
}

function serializeForInput(v) {
  if (v === null || v === undefined) return "";
  return String(v);
}
function parseFieldValue(type, raw) {
  if (type === "integer") return parseInt(raw, 10);
  return parseFloat(raw);
}
function buildInitialDraft(doc, fields, booleanFields, extraKeys) {
  const d = {};
  for (const f of fields) d[f.key] = serializeForInput(doc[f.key]);
  if (booleanFields) for (const b of booleanFields) d[b.key] = !!doc[b.key];
  if (extraKeys) {
    for (const k of extraKeys) {
      if (k === "perks") {
        // Array → newline-joined string for the textarea.
        d[k] = perksArrayToTextarea(doc[k]);
      } else {
        d[k] = serializeForInput(doc[k]);
      }
    }
  }
  return d;
}

// ---------------------------------------------------------------------------
// AffixInput — generic numeric input with $ / % affixes
// ---------------------------------------------------------------------------
function AffixInput({ field, value, onChange, error, testid }) {
  const isCurrency = field.type === "currency";
  const isPercent = field.type === "percent";
  return (
    <div className="relative">
      {isCurrency && (
        <span className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground">$</span>
      )}
      <Input
        type={field.type === "text" ? "text" : "number"}
        step={isCurrency ? "0.01" : isPercent ? "0.1" : "1"}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "h-11 rounded-full px-5",
          isCurrency && "pl-9",
          isPercent && "pr-9",
          error && "border-red-500 focus-visible:ring-red-500"
        )}
        data-testid={testid}
      />
      {isPercent && (
        <span className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground">%</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// (Removed: FeeFieldGroup) — Missed-Items Combined Batch (Item 3) /
// Phase F Stage 2 dropped `fee_type` + `fee_value` as editable admin
// inputs. The canonical commission rate lives on
// `partner_tiers.commission_pct` (read by
// `bookings.py::_resolve_tier_commission_pct`); admins tune it via
// the "Commission %" input rendered from `TIER_FIELDS`.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// BadgeSelect — dropdown rendering each preset as a colored pill + icon
// ---------------------------------------------------------------------------
function BadgeSelect({ value, onChange, testidPrefix }) {
  return (
    <div data-testid={`${testidPrefix}-badge-select`}>
      <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">Badge</Label>
      <Select value={value || "none"} onValueChange={onChange}>
        <SelectTrigger
          className="mt-2 h-11 rounded-full"
          data-testid={`${testidPrefix}-badge-trigger`}
        >
          <SelectValue placeholder="Pick a badge…">
            {value && value !== "none" ? (
              <BadgeChip badgeId={value} />
            ) : (
              <span className="text-pp-text-muted">None (no chip)</span>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent data-testid={`${testidPrefix}-badge-options`}>
          {BADGE_OPTIONS.map((id) => {
            const meta = badgeMeta(id);
            const Icon = badgeIconComponent(meta.icon);
            return (
              <SelectItem
                key={id}
                value={id}
                data-testid={`${testidPrefix}-badge-option-${id}`}
              >
                {id === "none" ? (
                  <span className="text-pp-text-muted">None (no chip)</span>
                ) : (
                  <span
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold",
                      badgeColorClasses(meta.color)
                    )}
                  >
                    {Icon && <Icon className="h-3 w-3" />}
                    {meta.label}
                  </span>
                )}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ExamplePayoutPanel — Section E. Live $40-booking math from the card's
// current draft (during edit) or canonical doc (read-only).
// ---------------------------------------------------------------------------
const EXAMPLE_BOOKING_TOTAL = 40.0;

function ExamplePayoutPanel({ feeType, feeValue, tierName, tierId, enabledFees, testidPrefix }) {
  const total = EXAMPLE_BOOKING_TOTAL;
  const numericFee = Number(feeValue || 0);
  const tierDeduction =
    feeType === "percent" ? (total * numericFee) / 100 : numericFee;
  const tierLine =
    feeType === "percent"
      ? `Platform fee (${tierName} · ${numericFee.toFixed(1)}%)`
      : `Platform fee (${tierName} · $${numericFee.toFixed(2)} flat)`;

  // Per-tier fee toggle: a fee applies to THIS tier iff fee.enabled AND
  // (tierId is in applies_to_tier_ids OR the field is missing — treat
  // legacy data as "applies everywhere" while the boot backfill runs).
  const appliesToThisTier = (f) => {
    if (!f.enabled) return false;
    const list = f.applies_to_tier_ids;
    if (!Array.isArray(list)) return true; // legacy / pre-backfill row
    if (!tierId) return true;               // safety: missing tierId prop
    return list.includes(tierId);
  };
  const perBooking = (enabledFees || []).filter(
    (f) => appliesToThisTier(f) && (f.basis === "per_booking_pct" || f.basis === "per_booking_flat")
  );
  const monthly = (enabledFees || []).filter((f) => appliesToThisTier(f) && f.basis === "monthly_flat");

  let payout = total - tierDeduction;
  for (const f of perBooking) {
    const v = Number(f.value || 0);
    payout -= f.basis === "per_booking_pct" ? (total * v) / 100 : v;
  }

  const renderFeeLine = (f) => {
    const v = Number(f.value || 0);
    const amt = f.basis === "per_booking_pct" ? (total * v) / 100 : v;
    const label = f.basis === "per_booking_pct" ? `${f.name} (${v.toFixed(1)}%)` : `${f.name} ($${v.toFixed(2)})`;
    return (
      <div className="flex justify-between text-sm" key={f.id} data-testid={`${testidPrefix}-example-fee-${f.id}`}>
        <span className="text-pp-text-muted">− {label}</span>
        <span className="font-mono tabular-nums text-pp-text">−${amt.toFixed(2)}</span>
      </div>
    );
  };

  return (
    <div
      className="mt-5 rounded-2xl border border-pp-border bg-pp-surface2 p-4"
      data-testid={`${testidPrefix}-example-panel`}
    >
      <div className="mb-3 flex items-center gap-2">
        <span className="inline-flex items-center rounded-full bg-pp-surface px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-pp-text-muted">
          Example
        </span>
        <span className="text-xs text-pp-text-muted">based on a $40.00 booking</span>
      </div>
      <div className="space-y-1.5">
        <div className="flex justify-between text-sm">
          <span className="text-pp-text">Booking total</span>
          <span className="font-mono tabular-nums text-pp-text">${total.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-pp-text-muted">− {tierLine}</span>
          <span className="font-mono tabular-nums text-pp-text">−${tierDeduction.toFixed(2)}</span>
        </div>
        {perBooking.map(renderFeeLine)}
        <hr className="my-2 border-pp-border" />
        <div className="flex justify-between text-sm font-bold">
          <span className="text-pp-text">= Estimated PooPartner payout</span>
          <span
            className={cn(
              "font-mono tabular-nums",
              payout < 0 ? "text-red-600" : "text-pp-text"
            )}
            data-testid={`${testidPrefix}-example-payout`}
          >
            ${payout.toFixed(2)}
          </span>
        </div>
      </div>
      {monthly.length > 0 && (
        <div className="mt-4 border-t border-pp-border pt-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-pp-text-muted">
            Monthly charges (not per booking)
          </p>
          <ul className="mt-1 space-y-0.5 text-xs text-pp-text-muted">
            {monthly.map((f) => (
              <li key={f.id} data-testid={`${testidPrefix}-example-monthly-${f.id}`}>
                · {f.name} ${Number(f.value).toFixed(2)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PerkChip — small chip used by the read-only plan card summary
// ---------------------------------------------------------------------------
function PerkChip({ children, color = "slate" }) {
  const cls =
    color === "orange"
      ? "bg-brand-orange/15 text-brand-orange"
      : "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200";
  return (
    <span className={cn("inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold", cls)}>
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// TaxonomyCard — per-row card supporting read-only + edit modes
// ---------------------------------------------------------------------------
function TaxonomyCard({
  doc,
  kind, // "tier" | "plan"
  fields,
  booleanFields,
  extraKeys,
  testidPrefix,
  onSave,
  onPromoteDefault,
  onAskDelete,
  saving,
  promoting,
  // Tier-specific:
  badgeSelectable,
  enabledFees,
  dragHandleProps,
  isDragging,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => buildInitialDraft(doc, fields, booleanFields, extraKeys));
  const [errors, setErrors] = useState({});

  // Sync draft when the canonical row changes (after Save).
  useEffect(() => {
    setDraft(buildInitialDraft(doc, fields, booleanFields, extraKeys));
    setErrors({});
  }, [doc, fields, booleanFields, extraKeys]);

  // Esc cancels edit mode.
  useEffect(() => {
    if (!editing) return;
    const h = (e) => {
      if (e.key === "Escape") cancel();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // `cancel` is a local function that closes over draft-clearing
    // setters (stable); adding it would re-add/remove the keydown
    // listener on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const setField = (key, v) => setDraft((s) => ({ ...s, [key]: v }));
  const setBool = (key, checked) => setDraft((s) => ({ ...s, [key]: !!checked }));
  const setBadgeId = (v) => setDraft((s) => ({ ...s, badge_id: v }));

  const badgeError = useMemo(() => {
    if (!badgeSelectable) return null;
    return validateBadgeId(draft.badge_id);
  }, [badgeSelectable, draft.badge_id]);

  const hasErrors = useMemo(
    () => Object.values(errors).some(Boolean) || !!badgeError,
    [errors, badgeError]
  );

  const dirty = useMemo(() => {
    for (const f of fields) {
      const before = serializeForInput(doc[f.key]);
      const after = String(draft[f.key] ?? "");
      if (before !== after) return true;
    }
    if (booleanFields) {
      for (const b of booleanFields) {
        if (!!doc[b.key] !== !!draft[b.key]) return true;
      }
    }
    if (extraKeys) {
      for (const k of extraKeys) {
        if (k === "perks") {
          if (!perksArraysEqual(doc.perks, perksTextareaToArray(draft.perks))) {
            return true;
          }
          continue;
        }
        const before = serializeForInput(doc[k]);
        const after = String(draft[k] ?? "");
        if (before !== after) return true;
      }
    }
    return false;
  }, [draft, doc, fields, booleanFields, extraKeys]);

  const validateAll = () => {
    const next = {};
    for (const f of fields) next[f.key] = f.validate(draft[f.key]);
    setErrors(next);
    return Object.values(next).every((e) => !e) && !badgeError;
  };

  const cancel = () => {
    setDraft(buildInitialDraft(doc, fields, booleanFields, extraKeys));
    setErrors({});
    setEditing(false);
  };

  const handleSave = async () => {
    if (!validateAll()) return;
    const payload = {};
    for (const f of fields) {
      const before = serializeForInput(doc[f.key]);
      const after = String(draft[f.key] ?? "");
      if (before === after) continue;
      payload[f.key] = f.type === "text" ? String(draft[f.key] ?? "").trim() : parseFieldValue(f.type, draft[f.key]);
    }
    if (booleanFields) {
      for (const b of booleanFields) {
        if (!!doc[b.key] !== !!draft[b.key]) payload[b.key] = !!draft[b.key];
      }
    }
    if (extraKeys) {
      if (extraKeys.includes("badge_id")) {
        const beforeB = String(doc.badge_id ?? "none");
        const afterB = String(draft.badge_id ?? "none");
        if (beforeB !== afterB) payload.badge_id = draft.badge_id;
      }
      if (extraKeys.includes("perks")) {
        const nextPerks = perksTextareaToArray(draft.perks);
        if (!perksArraysEqual(doc.perks, nextPerks)) {
          payload.perks = nextPerks;
        }
      }
    }
    if (Object.keys(payload).length === 0) {
      setEditing(false);
      return;
    }
    await onSave(doc.id, payload);
    setEditing(false);
  };

  // ----- Read-only headline ------------------------------------------------
  // Missed-Items Combined Batch (Item 3) / Phase F Stage 2 — the tier
  // headline reads the CANONICAL commission source (`commission_pct`),
  // not the removed `fee_value` / `fee_type` duplicate. Matches the
  // ExamplePayoutPanel + real booking math in `_compute_pricing`.
  let headline;
  if (kind === "plan") {
    headline = (
      <div className="flex items-baseline gap-2" data-testid={`${testidPrefix}-headline`}>
        <span className="font-heading text-4xl font-bold text-pp-text">
          ${Number(doc.monthly_price || 0).toFixed(2)}
        </span>
        <span className="text-sm font-semibold text-pp-text-muted">/ month</span>
      </div>
    );
  } else {
    headline = (
      <div className="flex items-baseline gap-2" data-testid={`${testidPrefix}-headline`}>
        <span className="font-heading text-4xl font-bold text-pp-text">
          {Number(doc.commission_pct || 0).toFixed(1)}%
        </span>
        <span className="text-sm font-semibold text-pp-text-muted">commission</span>
      </div>
    );
  }

  // ----- Read-only chips/perks --------------------------------------------
  //
  // Feb 2026 amendment (see PRD §Pricing Config Change Protocol): the
  // `member_discount_pct` chip WAS surfaced here to celebrate a value the
  // booking engine never actually applied (bookings.py:222 has the
  // placeholder). Surfacing a benefit the customer doesn't receive is a
  // truth-in-advertising failure — suppressed until the engine wires the
  // discount end-to-end. The field itself STAYS on the doc + edit form so
  // admin can still configure the future value ahead of the engine dispatch.
  //
  // xfail lock: `backend/tests/meta/test_pricing_config_integrity.py`
  // Family D asserts the platform-funded discount invariant with
  // `strict=True`, so re-surfacing this chip must land in the SAME
  // dispatch that flips the engine — otherwise the sentinel test flips
  // from xfail → xpass and the suite fails.
  const planChips = kind === "plan" ? (
    <div className="mt-3 flex flex-wrap gap-2">
      {doc.waive_securement_fee && <PerkChip>Securement waived</PerkChip>}
      {doc.priority_scheduling && <PerkChip>Priority scheduling</PerkChip>}
    </div>
  ) : null;

  const tierChips = kind === "tier" ? (
    <div className="mt-3 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <BadgeChip badgeId={doc.badge_id} testId={`${testidPrefix}-badge-chip`} />
        <PerkChip data-testid={`${testidPrefix}-commission-chip`}>
          {Number(doc.commission_pct || 0).toFixed(1)}% commission
        </PerkChip>
      </div>
      {Array.isArray(doc.perks) && doc.perks.length > 0 && (
        <ul
          className="ml-1 list-disc space-y-0.5 pl-4 text-sm text-pp-text-muted"
          data-testid={`${testidPrefix}-perks-list`}
        >
          {doc.perks.map((p, idx) => (
            <li key={idx} data-testid={`${testidPrefix}-perks-list-item-${idx}`}>
              {p}
            </li>
          ))}
        </ul>
      )}
    </div>
  ) : null;

  const lastUpdated = doc.updated_at ? (
    <p className="mt-3 text-xs italic text-pp-text-muted">
      Last updated: {String(doc.updated_at).slice(0, 16).replace("T", " ")}
    </p>
  ) : null;

  const isDefault = !!doc.is_default;

  return (
    <Card
      className={cn(
        "rounded-2xl border border-pp-border bg-pp-surface p-6 transition",
        isDragging && "ring-2 ring-brand-orange/40 shadow-lg"
      )}
      style={{ boxShadow: "var(--pp-card-shadow)" }}
      data-testid={`${testidPrefix}-card`}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          {dragHandleProps && (
            <button
              type="button"
              className="mt-0.5 cursor-grab rounded-md p-1 text-slate-400 hover:bg-pp-surface2 hover:text-brand-orange active:cursor-grabbing"
              aria-label="Drag to reorder"
              {...dragHandleProps}
              data-testid={`${testidPrefix}-drag-handle`}
            >
              <GripVertical className="h-5 w-5" />
            </button>
          )}
          <div className="min-w-0">
            <h3 className="font-heading text-2xl font-bold text-pp-text" data-testid={`${testidPrefix}-name`}>
              {doc.name}
            </h3>
            {isDefault && (
              <span
                className="mt-1 inline-flex items-center gap-1 rounded-full bg-brand-orange/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-orange"
                data-testid={`${testidPrefix}-default-pill`}
              >
                <Star className="h-3 w-3" /> Default
              </span>
            )}
          </div>
        </div>
        <div className="text-right">{headline}</div>
      </div>

      {/* ---- READ-ONLY MODE ---- */}
      {!editing && (
        <>
          {tierChips}
          {planChips}
          {lastUpdated}

          {/* Example payout panel (tier only) — read-only uses the
              CANONICAL commission source (`commission_pct`), not the
              legacy `fee_value`/`fee_type` duplicate. See admin_taxonomy.py
              Leg 3 migration comment for the full history. This ensures
              the admin UI's example-fee calculation matches what
              `_compute_pricing` actually commissions on real bookings. */}
          {kind === "tier" && (
            <ExamplePayoutPanel
              feeType="percent"
              feeValue={doc.commission_pct}
              tierName={doc.name}
              tierId={doc.id}
              enabledFees={enabledFees}
              testidPrefix={testidPrefix}
            />
          )}

          <div className="mt-5 flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onAskDelete(doc)}
              disabled={isDefault}
              className="rounded-full text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40"
              data-testid={`${testidPrefix}-delete`}
            >
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </Button>
            {!isDefault && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => onPromoteDefault(doc.id)}
                disabled={promoting}
                className="rounded-full"
                data-testid={`${testidPrefix}-set-default`}
              >
                {promoting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Set as default
              </Button>
            )}
            <Button
              type="button"
              onClick={() => setEditing(true)}
              className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
              data-testid={`${testidPrefix}-edit`}
              aria-expanded={editing}
            >
              Edit
            </Button>
          </div>
        </>
      )}

      {/* ---- EDIT MODE ---- */}
      {editing && (
        <>
          <hr className="my-5 border-pp-border" />

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {fields.map((f) => (
              <div key={f.key}>
                <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
                  {f.label}
                </Label>
                <div className="mt-2">
                  <AffixInput
                    field={f}
                    value={draft[f.key]}
                    onChange={(v) => setField(f.key, v)}
                    error={errors[f.key]}
                    testid={`${testidPrefix}-${f.key.replace(/_/g, "-")}-input`}
                  />
                </div>
                {errors[f.key] && (
                  <p
                    className="mt-1 text-xs font-semibold text-red-600"
                    data-testid={`${testidPrefix}-${f.key.replace(/_/g, "-")}-error`}
                  >
                    {errors[f.key]}
                  </p>
                )}
              </div>
            ))}
            {badgeSelectable && (
              <BadgeSelect
                value={draft.badge_id}
                onChange={setBadgeId}
                testidPrefix={testidPrefix}
              />
            )}
          </div>

          {booleanFields && (
            <div className="mt-5 space-y-3">
              {booleanFields.map((b) => (
                <label key={b.key} className="flex items-center gap-2 text-sm text-pp-text">
                  <Checkbox
                    checked={!!draft[b.key]}
                    onCheckedChange={(c) => setBool(b.key, c)}
                    data-testid={`${testidPrefix}-${b.key.replace(/_/g, "-")}-checkbox`}
                  />
                  {b.label}
                </label>
              ))}
            </div>
          )}

          {extraKeys && extraKeys.includes("perks") && (
            <div className="mt-5" data-testid={`${testidPrefix}-perks-edit`}>
              <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
                Perks (one per line)
              </Label>
              <textarea
                value={draft.perks ?? ""}
                onChange={(e) => setField("perks", e.target.value)}
                rows={4}
                placeholder={"Higher placement\nTrusted PooPartner badge"}
                className="mt-2 w-full rounded-2xl border border-pp-border bg-pp-surface px-4 py-3 text-sm text-pp-text shadow-sm placeholder:text-pp-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange"
                data-testid={`${testidPrefix}-perks-input`}
              />
              <p className="mt-1 text-xs text-pp-text-muted">
                Free-text bullet lines surfaced on the public tier card. Blank lines are dropped.
              </p>
            </div>
          )}

          {/* Example payout in edit mode uses LIVE draft commission_pct
              for realtime preview. Per Jul 2026 fix, both edit-mode
              AND read-only surfaces read the CANONICAL commission
              source — not the legacy `fee_value` duplicate. */}
          {kind === "tier" && (
            <ExamplePayoutPanel
              feeType="percent"
              feeValue={draft.commission_pct ?? doc.commission_pct}
              tierName={draft.name || doc.name}
              tierId={doc.id}
              enabledFees={enabledFees}
              testidPrefix={testidPrefix}
            />
          )}

          <div className="mt-5 flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onAskDelete(doc)}
              disabled={isDefault}
              className="rounded-full text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40"
              data-testid={`${testidPrefix}-delete`}
            >
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={cancel}
              className="rounded-full"
              data-testid={`${testidPrefix}-cancel`}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={!dirty || hasErrors || saving}
              className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
              data-testid={`${testidPrefix}-save`}
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// AddRowDialog — used by both tier + plan sections
// ---------------------------------------------------------------------------
function AddRowDialog({
  open,
  onOpenChange,
  title,
  fields,
  booleanFields,
  extraKeys,
  defaults,
  badgeSelectable,
  submitLabel,
  onSubmit,
  testidPrefix,
}) {
  const [draft, setDraft] = useState(defaults);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(defaults);
      setErrors({});
    }
  }, [open, defaults]);

  const badgeError = useMemo(() => {
    if (!badgeSelectable) return null;
    return validateBadgeId(draft.badge_id);
  }, [badgeSelectable, draft.badge_id]);

  const setField = (key, v) => setDraft((s) => ({ ...s, [key]: v }));
  const setBool = (key, checked) => setDraft((s) => ({ ...s, [key]: !!checked }));

  const validateAll = () => {
    const next = {};
    for (const f of fields) next[f.key] = f.validate(draft[f.key]);
    setErrors(next);
    return Object.values(next).every((e) => !e) && !badgeError;
  };

  const submit = async () => {
    if (!validateAll()) return;
    const payload = {};
    for (const f of fields) {
      if (f.type === "text") payload[f.key] = String(draft[f.key] ?? "").trim();
      else payload[f.key] = parseFieldValue(f.type, draft[f.key]);
    }
    if (booleanFields) for (const b of booleanFields) payload[b.key] = !!draft[b.key];
    if (extraKeys) {
      if (extraKeys.includes("badge_id")) payload.badge_id = draft.badge_id || "none";
    }
    setSubmitting(true);
    try {
      await onSubmit(payload);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl bg-pp-surface" data-testid={`${testidPrefix}-add-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-pp-text">{title}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {fields.map((f) => (
            <div key={f.key}>
              <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
                {f.label}
              </Label>
              <div className="mt-2">
                <AffixInput
                  field={f}
                  value={draft[f.key]}
                  onChange={(v) => setField(f.key, v)}
                  error={errors[f.key]}
                  testid={`${testidPrefix}-add-${f.key.replace(/_/g, "-")}-input`}
                />
              </div>
              {errors[f.key] && (
                <p className="mt-1 text-xs font-semibold text-red-600">{errors[f.key]}</p>
              )}
            </div>
          ))}
          {badgeSelectable && (
            <BadgeSelect
              value={draft.badge_id}
              onChange={(v) => setDraft((s) => ({ ...s, badge_id: v }))}
              testidPrefix={`${testidPrefix}-add`}
            />
          )}
          {booleanFields && (
            <div className="sm:col-span-2 space-y-3">
              {booleanFields.map((b) => (
                <label key={b.key} className="flex items-center gap-2 text-sm text-pp-text">
                  <Checkbox
                    checked={!!draft[b.key]}
                    onCheckedChange={(c) => setBool(b.key, c)}
                  />
                  {b.label}
                </label>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={submitting || !!badgeError}
            className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
            data-testid={`${testidPrefix}-add-submit`}
          >
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// TaxonomySection — generic shell, with optional DnD wrapper for tiers
// ---------------------------------------------------------------------------
function TaxonomySection({
  kind,
  title,
  subtitle,
  Icon,
  testidPrefix,
  endpoint,
  fields,
  booleanFields,
  extraKeys,
  badgeSelectable,
  sortable,
  addTitle,
  addLabel,
  toastSaved,
  toastAdded,
  toastDeleted,
  emptyAddDefaults,
  deprecationBanner = null,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [promotingId, setPromotingId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [reorderBusy, setReorderBusy] = useState(false);

  // Tier section also needs platform_fees so the Example panel can compute
  // realistic deductions from enabled per-booking fees.
  const [enabledFees, setEnabledFees] = useState([]);

  const reload = async () => {
    try {
      const { data } = await api.get(endpoint);
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    if (kind === "tier") {
      // INTENTIONAL second consumer of /admin/platform-fees: the Tier
      // example-payout panel needs the enabled fee list to render its
      // live $40-booking math. PlatformFeesSection also fetches the
      // same list for its own card list. Two consumers, one fetch each
      // = 2 calls per page load. Documented and within the agreed
      // "max 2" budget. Lifting to a shared hook is on the backlog if
      // we need to shave further.
      api
        .get("/admin/platform-fees")
        .then(({ data }) => setEnabledFees(data.filter((f) => f.enabled)))
        .catch(() => setEnabledFees([]));
    }
    // `reload` closes over `endpoint`/`kind` (already deps) + row-setters
    // (stable); adding it would infinite-loop on every fetch completion.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, kind]);

  const save = async (id, payload) => {
    setSavingId(id);
    try {
      const { data } = await api.put(`${endpoint}/${id}`, payload);
      setRows((rs) => rs.map((r) => (r.id === id ? data : r)));
      toast.success(toastSaved);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingId(null);
    }
  };

  const promoteDefault = async (id) => {
    setPromotingId(id);
    try {
      const { data } = await api.put(`${endpoint}/${id}`, { is_default: true });
      // Server demoted all others atomically; refresh whole list.
      await reload();
      setRows((rs) => rs.map((r) => (r.id === id ? data : r)));
      toast.success(toastSaved);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPromotingId(null);
    }
  };

  const submitAdd = async (body) => {
    try {
      const { data } = await api.post(endpoint, body);
      setRows((rs) => [...rs, data].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
      toast.success(toastAdded);
      setAddOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`${endpoint}/${deleteTarget.id}`);
      setRows((rs) => rs.filter((r) => r.id !== deleteTarget.id));
      toast.success(toastDeleted);
      setDeleteTarget(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    const next = Array.from(rows);
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    setRows(next); // optimistic
    setReorderBusy(true);
    try {
      const { data } = await api.put(`${endpoint}/reorder`, {
        order: next.map((r) => r.id),
      });
      setRows(data);
      toast.success("Reordered.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      await reload();
    } finally {
      setReorderBusy(false);
    }
  };

  const renderCard = (r, dragHandleProps, isDragging) => (
    <TaxonomyCard
      key={r.id}
      doc={r}
      kind={kind}
      fields={fields}
      booleanFields={booleanFields}
      extraKeys={extraKeys}
      badgeSelectable={badgeSelectable}
      enabledFees={enabledFees}
      testidPrefix={`${testidPrefix}-${r.id}`}
      onSave={save}
      onPromoteDefault={promoteDefault}
      onAskDelete={setDeleteTarget}
      saving={savingId === r.id}
      promoting={promotingId === r.id}
      dragHandleProps={dragHandleProps}
      isDragging={isDragging}
    />
  );

  return (
    <section className="mt-2" data-testid={`${testidPrefix}-section`}>
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Icon className="h-6 w-6 text-brand-orange" aria-hidden="true" />
            <h2 className="font-heading text-2xl font-semibold text-pp-text">{title}</h2>
          </div>
          <p className="mt-1 text-sm text-pp-text-muted">{subtitle}</p>
        </div>
        <Button
          type="button"
          onClick={() => setAddOpen(true)}
          className="h-11 rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
          data-testid={`${kind}-add-button`}
        >
          <Plus className="mr-2 h-4 w-4" /> {addLabel}
        </Button>
      </header>

      {/* Phase F Stage 1 — legacy-field deprecation banner. Renders
          just under the section header when the parent passed a
          `deprecationBanner` config. Yellow surface + factual copy
          (no defensive framing) — points admins at the canonical
          replacements the booking engine actually reads. */}
      {deprecationBanner && (
        <div
          role="note"
          className="flex flex-wrap items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          data-testid={deprecationBanner.testId}
        >
          <span className="mt-0.5 text-lg leading-none" aria-hidden="true">⚠</span>
          <div className="flex-1">
            <p className="font-semibold">
              Deprecated fields:{" "}
              <code className="font-mono text-[0.85em]">
                {deprecationBanner.fields.join(", ")}
              </code>
            </p>
            <p className="mt-1">{deprecationBanner.message}</p>
          </div>
        </div>
      )}

      {loading ? (
        <p className="flex items-center gap-2 text-sm text-pp-text-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </p>
      ) : sortable ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId={`${testidPrefix}-droppable`}>
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="space-y-4"
                data-testid={`${testidPrefix}-list`}
                aria-busy={reorderBusy}
              >
                {rows.map((r, idx) => (
                  <Draggable key={r.id} draggableId={r.id} index={idx}>
                    {(p, snap) => (
                      <div
                        ref={p.innerRef}
                        {...p.draggableProps}
                        style={p.draggableProps.style}
                      >
                        {renderCard(r, p.dragHandleProps, snap.isDragging)}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <div className="space-y-4">{rows.map((r) => renderCard(r, null, false))}</div>
      )}

      <AddRowDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        title={addTitle}
        fields={fields}
        booleanFields={booleanFields}
        extraKeys={extraKeys}
        badgeSelectable={badgeSelectable}
        defaults={emptyAddDefaults}
        submitLabel={addLabel.replace("Add ", "Create ")}
        onSubmit={submitAdd}
        testidPrefix={kind}
      />

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="rounded-3xl bg-pp-surface" data-testid={`${kind}-delete-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-pp-text">
              Delete &ldquo;{deleteTarget?.name}&rdquo;?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-pp-text-muted">This cannot be undone.</p>
          <DialogFooter>
            <Button variant="ghost" type="button" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmDelete}
              className="rounded-full bg-red-600 text-white hover:bg-red-700"
              data-testid={`${kind}-delete-confirm`}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Public exports
// ---------------------------------------------------------------------------
export function PartnerTiersSection() {
  return (
    <TaxonomySection
      kind="tier"
      title="PooPartner Tiers"
      subtitle="Drag to reorder. Each tier's example payout updates live while editing."
      Icon={Award}
      testidPrefix="partner-tier"
      endpoint="/admin/partner-tiers"
      fields={TIER_FIELDS}
      booleanFields={null}
      extraKeys={TIER_EXTRA_KEYS}
      badgeSelectable={true}
      sortable={true}
      addTitle="New PooPartner tier"
      addLabel="Add tier"
      toastSaved="Tier updated."
      toastAdded="Tier created."
      toastDeleted="Tier deleted."
      emptyAddDefaults={{
        name: "",
        commission_pct: "10",
        badge_id: "none",
      }}
      /* Missed-Items Combined Batch (Item 3) — Phase F Stage 2
         completes the field removal: `monthly_price`, `fee_value`,
         and `fee_type` are gone from the tier admin UI. The banner
         from Stage 1 was removed with them (nothing left to warn
         about). Backend response schema is UNCHANGED — bookings.py
         still resolves commission from `commission_pct`. */
    />
  );
}

export function CustomerPlansSection() {
  return (
    <TaxonomySection
      kind="plan"
      title="Customer Plans"
      subtitle="Drag to reorder. Define the demand-side membership plans. Assignment to customers is not yet enabled."
      Icon={Users}
      testidPrefix="customer-plan"
      endpoint="/admin/customer-plans"
      fields={PLAN_FIELDS}
      booleanFields={PLAN_BOOLEAN_FIELDS}
      extraKeys={null}
      badgeSelectable={false}
      sortable={true}
      addTitle="New Customer plan"
      addLabel="Add plan"
      toastSaved="Plan updated."
      toastAdded="Plan created."
      toastDeleted="Plan deleted."
      emptyAddDefaults={{
        name: "",
        sort_order: "10",
        monthly_price: "0",
        member_discount_pct: "0",
        waive_securement_fee: false,
        priority_scheduling: false,
      }}
    />
  );
}
